import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/memories.css';
import DeleteButton from '../components/DeleteButton';
import { API_URL } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const Memories = () => {
  const { user } = useAuth();
  const [memories, setMemories] = useState([]);
  const [newMemory, setNewMemory] = useState({
    title: '',
    description: '',
    photo: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchMemories();
  }, []);

  const fetchMemories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/memories`);
      setMemories(response.data);
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch memories');
      setLoading(false);
    }
  };

  // Restrict access for unpartnered or soft-deleted users
  if (!user?.partner || user?.deletionRequestedAt) {
    return <div style={{textAlign: 'center', marginTop: 40, color: '#888'}}>You must be in a couple to view memories.</div>;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewMemory(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhotoChange = (e) => {
    setNewMemory(prev => ({
      ...prev,
      photo: e.target.files[0]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newMemory.photo) {
      setError('Please select a photo.');
      return;
    }
    const formData = new FormData();
    formData.append('title', newMemory.title);
    formData.append('description', newMemory.description);
    if (newMemory.photo) {
      formData.append('photo', newMemory.photo);
    }

    try {
      setSubmitting(true);
      await axios.post(`${API_URL}/api/memories`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setNewMemory({ title: '', description: '', photo: null });
      setShowModal(false);
      fetchMemories();
    } catch (error) {
      setError('Failed to add memory');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMemoryDeleted = (deletedId) => {
    setMemories(prev => prev.filter(memory => memory._id !== deletedId));
  };

  if (loading) {
    return <div>Loading memories...</div>;
  }

  return (
    <div className="memories-container card">
      <h2 className="memories-title">Our Memories</h2>
      {error && <div className="error-message">{error}</div>}

      <div className="memories-grid memories-list">
        {memories.map(memory => (
          <div key={memory._id} className="memory-card card">
            {memory.photo && (
              <img src={memory.photo} alt={memory.title} className="memory-photo" />
            )}
            <div className="memory-info">
              <div className="memory-title">{memory.title}</div>
              <div className="memory-desc">{memory.description}</div>
              <div className="memory-footer">
                <div className="memory-meta">{new Date(memory.createdAt).toLocaleDateString()}</div>
                <DeleteButton 
                  itemId={memory._id} 
                  itemType="memory" 
                  onSuccess={handleMemoryDeleted}
                  title="Delete Memory"
                  message="Are you sure you want to delete this memory? This action cannot be undone."
                  size="small"
                  photoUrl={memory.photo}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Floating Action Button */}
      <button 
        className={`fab-add-memory ${showModal ? 'fab-active' : ''} btn-gradient`} 
        onClick={() => setShowModal(true)} 
        title="Add Memory"
        disabled={submitting}
      >
        {submitting ? <span className="fab-loader"></span> : '+'}
      </button>

      {/* Modal Popup for Add Memory Form */}
      {showModal && (
        <div className="memories-modal-overlay" onClick={() => !submitting && setShowModal(false)}>
          <div className="memories-modal-content" onClick={e => e.stopPropagation()}>
            <button 
              className="memories-modal-close" 
              onClick={() => !submitting && setShowModal(false)}
              disabled={submitting}
            >
              &times;
            </button>
            <h3 className="memories-modal-title">Add New Memory</h3>
            <form onSubmit={handleSubmit} className="memory-form" autoComplete="off">
              <div className="form-group">
                <input
                  type="text"
                  name="title"
                  value={newMemory.title}
                  onChange={handleChange}
                  placeholder=" "
                  required
                />
                <label>Memory Title</label>
              </div>
              <div className="form-group">
                <textarea
                  name="description"
                  value={newMemory.description}
                  onChange={handleChange}
                  placeholder=" "
                  rows="4"
                  required
                />
                <label>Describe this memory...</label>
              </div>
              <div className="form-group">
                <label htmlFor="memory-photo" className="custom-file-label">
                  Choose Photo
                  <input
                    id="memory-photo"
                    type="file"
                    name="photo"
                    accept="image/*"
                    onChange={handlePhotoChange}
                  />
                </label>
                {newMemory.photo && (
                  <span className="selected-file-name">{newMemory.photo.name}</span>
                )}
              </div>
              <button 
                type="submit" 
                className="submit-btn add-memory-btn btn btn-gradient"
                disabled={submitting}
              >
                {submitting ? 'Adding...' : 'Add Memory'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Memories; 